import * as React from "react";
import {
  Animated,
  type GestureResponderEvent,
  type LayoutChangeEvent,
  Platform,
  Pressable,
  type PressableAndroidRippleConfig,
  type PressableProps,
  type StyleProp,
  StyleSheet,
  type TextStyle,
  View,
  type ViewStyle,
} from "react-native";
import useLatestCallback from "use-latest-callback";
import type { NavigationState, Route, TabDescriptor } from "react-native-tab-view";
import { useTheme } from "@/hooks/useTheme";

// ---------------------------------------------------------------------------
// PlatformPressable (inlined from react-native-tab-view)
// ---------------------------------------------------------------------------

type PlatformPressableProps = Omit<PressableProps, "onPress"> & {
  href?: string;
  pressColor?: string;
  pressOpacity?: number;
  onPress?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | GestureResponderEvent) => void;
  children: React.ReactNode;
};

const ANDROID_VERSION_LOLLIPOP = 21;
const ANDROID_SUPPORTS_RIPPLE = Platform.OS === "android" && Platform.Version >= ANDROID_VERSION_LOLLIPOP;

function PlatformPressable({
  disabled,
  android_ripple,
  pressColor = "rgba(0, 0, 0, .32)",
  pressOpacity,
  style,
  onPress,
  ...rest
}: PlatformPressableProps) {
  const handlePress = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | GestureResponderEvent) => {
    if (Platform.OS === "web" && rest.href !== null) {
      const hasModifierKey =
        ("metaKey" in e && e.metaKey) ||
        ("altKey" in e && e.altKey) ||
        ("ctrlKey" in e && e.ctrlKey) ||
        ("shiftKey" in e && e.shiftKey);
      const isLeftClick = "button" in e ? e.button == null || e.button === 0 : true;
      const isSelfTarget =
        e.currentTarget && "target" in e.currentTarget
          ? [undefined, null, "", "self"].includes(e.currentTarget.target as string)
          : true;
      if (!hasModifierKey && isLeftClick && isSelfTarget) {
        e.preventDefault();
        onPress?.(e);
      }
    } else {
      onPress?.(e);
    }
  };

  return (
    <Pressable
      android_ripple={ANDROID_SUPPORTS_RIPPLE && !disabled ? { color: pressColor, ...android_ripple } : undefined}
      style={(state) => [
        {
          cursor: (Platform.OS === "web" || Platform.OS === "ios") && !disabled ? "pointer" : ("auto" as any),
          opacity: !ANDROID_SUPPORTS_RIPPLE && state.pressed && !disabled ? pressOpacity : 1,
        } as const,
        typeof style === "function" ? style(state) : style,
      ]}
      onPress={disabled ? undefined : handlePress}
      {...rest}
    />
  );
}

// ---------------------------------------------------------------------------
// TabBarItemLabel (inlined from react-native-tab-view)
// ---------------------------------------------------------------------------

const TabBarItemLabel = React.memo(function TabBarItemLabel({
  label,
  style,
  icon,
  focused,
}: {
  label?: string;
  style: StyleProp<TextStyle>;
  icon: React.ReactNode;
  focused?: boolean;
}) {
  const { colors } = useTheme();
  if (!label) return null;
  return (
    <View style={{ justifyContent: "flex-end" }}>
      {/* Hidden bold text reserves width so the tab doesn't shift on focus */}
      <Animated.Text
        style={[styles.labelText, icon ? { marginTop: 0 } : null, { fontWeight: "700", color: "transparent" }, style]}
        aria-hidden
      >
        {label}
      </Animated.Text>
      <Animated.Text
        style={[
          styles.labelText,
          StyleSheet.absoluteFill,
          icon ? { marginTop: 0 } : null,
          focused && { fontWeight: "700" },
          { color: colors.text },
          style,
        ]}
      >
        {label}
      </Animated.Text>
    </View>
  );
});

// ---------------------------------------------------------------------------
// DrawerTabBarItem
// ---------------------------------------------------------------------------

const DEFAULT_ACTIVE_COLOR = "rgba(255, 255, 255, 1)";
const DEFAULT_INACTIVE_COLOR = "rgba(255, 255, 255, 0.7)";
const ICON_SIZE = 24;

const getActiveOpacity = (position: Animated.AnimatedInterpolation<number>, routesLength: number, tabIndex: number) => {
  if (routesLength > 1) {
    const inputRange = Array.from({ length: routesLength }, (_, i) => i);
    return position.interpolate({
      inputRange,
      outputRange: inputRange.map((i) => (i === tabIndex ? 1 : 0)),
    });
  }
  return 1;
};

const getInactiveOpacity = (
  position: Animated.AnimatedInterpolation<number>,
  routesLength: number,
  tabIndex: number,
) => {
  if (routesLength > 1) {
    const inputRange = Array.from({ length: routesLength }, (_, i) => i);
    return position.interpolate({
      inputRange,
      outputRange: inputRange.map((i: number) => (i === tabIndex ? 0 : 1)),
    });
  }
  return 0;
};

export type DrawerTabBarItemProps<T extends Route> = TabDescriptor<T> & {
  position: Animated.AnimatedInterpolation<number>;
  route: T;
  navigationState: NavigationState<T>;
  activeColor?: string;
  inactiveColor?: string;
  pressColor?: string;
  pressOpacity?: number;
  onLayout?: (event: LayoutChangeEvent) => void;
  onPress: () => void;
  onLongPress: () => void;
  defaultTabWidth?: number;
  style: StyleProp<ViewStyle>;
  android_ripple?: PressableAndroidRippleConfig;
  /** Content rendered inline as a sibling of icon & label inside the inner container */
  inlineBadge?: React.ReactNode;
  /** Style applied to the inner container that wraps icon + label + inlineBadge (e.g. hover background) */
  innerContainerStyle?: StyleProp<ViewStyle>;
  /** Called when pointer enters the inner container */
  onInnerPointerEnter?: () => void;
  /** Called when pointer leaves the inner container */
  onInnerPointerLeave?: () => void;
};

type InternalProps<T extends Route> = Omit<
  DrawerTabBarItemProps<T>,
  "navigationState" | "getAccessibilityLabel" | "getLabelText" | "getTestID" | "getAccessible" | "options"
> & {
  isFocused: boolean;
  index: number;
  routesLength: number;
} & TabDescriptor<T>;

const ANDROID_RIPPLE_DEFAULT = { borderless: true };

const DrawerTabBarItemInternal = <T extends Route>({
  accessibilityLabel,
  accessible,
  label: customlabel,
  testID,
  onLongPress,
  onPress,
  isFocused,
  position,
  style,
  inactiveColor: inactiveColorCustom,
  activeColor: activeColorCustom,
  labelStyle,
  onLayout,
  index: tabIndex,
  pressColor,
  pressOpacity,
  defaultTabWidth,
  icon: customIcon,
  badge: customBadge,
  href,
  labelText,
  routesLength,
  android_ripple = ANDROID_RIPPLE_DEFAULT,
  labelAllowFontScaling,
  route,
  inlineBadge,
  innerContainerStyle,
  onInnerPointerEnter,
  onInnerPointerLeave,
}: InternalProps<T>) => {
  const labelColorFromStyle = StyleSheet.flatten(labelStyle || {}).color;

  const activeColor =
    activeColorCustom !== undefined
      ? activeColorCustom
      : typeof labelColorFromStyle === "string"
        ? labelColorFromStyle
        : DEFAULT_ACTIVE_COLOR;
  const inactiveColor =
    inactiveColorCustom !== undefined
      ? inactiveColorCustom
      : typeof labelColorFromStyle === "string"
        ? labelColorFromStyle
        : DEFAULT_INACTIVE_COLOR;

  const activeOpacity = getActiveOpacity(position, routesLength, tabIndex);
  const inactiveOpacity = getInactiveOpacity(position, routesLength, tabIndex);

  const icon = React.useMemo(() => {
    if (!customIcon) return null;

    const inactiveIcon = customIcon({ focused: false, color: inactiveColor, size: ICON_SIZE, route });
    const activeIcon = customIcon({ focused: true, color: activeColor, size: ICON_SIZE, route });

    return (
      <View style={[styles.icon, { opacity: 0.75 }]}>
        <Animated.View style={{ opacity: inactiveOpacity }}>{inactiveIcon}</Animated.View>
        <Animated.View style={[StyleSheet.absoluteFill, { opacity: activeOpacity }]}>{activeIcon}</Animated.View>
      </View>
    );
  }, [activeColor, activeOpacity, customIcon, inactiveColor, inactiveOpacity, route]);

  const renderLabel = React.useCallback(
    (focused: boolean) =>
      customlabel ? (
        customlabel({
          focused,
          color: focused ? activeColor : inactiveColor,
          style: labelStyle,
          labelText,
          allowFontScaling: labelAllowFontScaling,
          route,
        })
      ) : (
        <TabBarItemLabel icon={icon} label={labelText} style={labelStyle} focused={isFocused} />
      ),
    [customlabel, activeColor, labelStyle, labelText, labelAllowFontScaling, route, inactiveColor, icon, isFocused],
  );

  const tabStyle = StyleSheet.flatten(style);
  const isWidthSet = tabStyle?.width !== undefined;
  const tabContainerStyle: ViewStyle | null = isWidthSet ? null : { width: defaultTabWidth };

  const ariaLabel = typeof accessibilityLabel !== "undefined" ? accessibilityLabel : labelText;

  return (
    <PlatformPressable
      android_ripple={android_ripple}
      testID={testID}
      accessible={accessible}
      role="tab"
      aria-label={ariaLabel}
      aria-selected={isFocused}
      pressColor={pressColor}
      pressOpacity={pressOpacity}
      unstable_pressDelay={0}
      onLayout={onLayout}
      onPress={onPress}
      onLongPress={onLongPress}
      href={href}
      style={[styles.pressable, tabContainerStyle]}
    >
      <View pointerEvents="none" style={[styles.item, tabStyle]}>
        <View
          pointerEvents="auto"
          onPointerEnter={onInnerPointerEnter}
          onPointerLeave={onInnerPointerLeave}
          style={[styles.innerContainer, innerContainerStyle]}
        >
          {icon}
          <View>
            <Animated.View style={{ opacity: inactiveOpacity }}>{renderLabel(false)}</Animated.View>
            <Animated.View style={[StyleSheet.absoluteFill, { opacity: activeOpacity }]}>
              {renderLabel(true)}
            </Animated.View>
          </View>
          {inlineBadge != null ? inlineBadge : null}
        </View>
        {/* OOB absolute badge (original behavior) */}
        {customBadge != null ? <View style={styles.badge}>{customBadge({ route })}</View> : null}
      </View>
    </PlatformPressable>
  );
};

const MemoizedInternal = React.memo(DrawerTabBarItemInternal) as typeof DrawerTabBarItemInternal;

export function DrawerTabBarItem<T extends Route>(props: DrawerTabBarItemProps<T>) {
  const { onPress, onLongPress, onLayout, navigationState, route, ...rest } = props;

  const onPressLatest = useLatestCallback(onPress);
  const onLongPressLatest = useLatestCallback(onLongPress);
  const onLayoutLatest = useLatestCallback(onLayout ? onLayout : () => {});

  const tabIndex = navigationState.routes.indexOf(route);

  return (
    <MemoizedInternal
      {...rest}
      onPress={onPressLatest}
      onLayout={onLayoutLatest}
      onLongPress={onLongPressLatest}
      isFocused={navigationState.index === tabIndex}
      route={route}
      index={tabIndex}
      routesLength={navigationState.routes.length}
    />
  );
}

const styles = StyleSheet.create({
  icon: {
    margin: 2,
  },
  item: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    paddingHorizontal: 3,
    minHeight: 48,
  },
  innerContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 7,
    borderRadius: 6,
  },
  badge: {
    position: "absolute",
    top: 0,
    end: 0,
  },
  pressable: {
    backgroundColor: "transparent",
    ...Platform.select({
      ios: { borderRadius: 10 },
      default: null,
    }),
  },
  labelText: {
    marginHorizontal: 4,
    fontSize: 14,
    fontWeight: "500",
    backgroundColor: "transparent",
  },
});
